/**
 * The name of a fabric book or of a sewing pattern.
 */
import { useState, type SubmitEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { ApiError } from '@/shared/api/client';
import { Dialog } from '@/shared/ui/Dialog';
import type { NamedItem, NamedItemService } from './api';
import { validateNamedItemName } from './named-item-form';

export interface NamedItemDialogProps {
  service: NamedItemService;
  item: NamedItem | null;
  /** Name pre-filled when creating (the sewing patterns start with "BDP "). */
  initialName?: string | undefined;
  testIdPrefix: string;
  onClose: (result: 'Confirm' | 'Cancel') => void;
}

/** A duplicate (409), else the API message when it sent one, else a generic message. */
const saveErrorMessage = (
  error: unknown,
  label: string,
  creating: boolean,
): string => {
  if (error instanceof ApiError) {
    if (error.status === 409) return `This ${label} already exists.`;
    const body = error.body as { message?: unknown } | string | null;
    if (body && typeof body === 'object' && typeof body.message === 'string')
      return body.message;
    if (typeof body === 'string') return body;
  }
  return creating ? `Failed to create ${label}` : `Failed to update ${label}`;
};

export function NamedItemDialog({
  service,
  item,
  initialName = '',
  testIdPrefix,
  onClose,
}: Readonly<NamedItemDialogProps>) {
  const creating = !item;
  const [name, setName] = useState(item?.name ?? initialName);
  const [dirty, setDirty] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const nameError = validateNamedItemName(name);
  const inputId = `${service.label.replaceAll(' ', '-')}-name`;

  const save = useMutation({
    mutationFn: () =>
      creating ? service.create(name) : service.update(item.id, name),
    onSuccess: () => onClose('Confirm'),
    onError: (error: unknown) =>
      setErrorMessage(saveErrorMessage(error, service.label, creating)),
  });

  const onSubmit = (event: SubmitEvent) => {
    event.preventDefault();
    setSubmitted(true);
    setErrorMessage(null);
    if (nameError) {
      setErrorMessage('Please fix the errors');
      return;
    }
    save.mutate();
  };

  return (
    <Dialog
      title={creating ? `Add a ${service.label}` : `Edit a ${service.label}`}
      width="500px"
      onClose={() => onClose('Cancel')}
      testId={`${testIdPrefix}-dialog`}
    >
      <form className="form popin-form" onSubmit={onSubmit} noValidate>
        <div className="mb-2">
          <label className="field-label" htmlFor={inputId}>
            Name
          </label>
          <input
            id={inputId}
            data-testid={`${testIdPrefix}-form-name`}
            className={`field ${dirty && nameError ? 'invalid' : ''}`}
            name="name"
            placeholder={`${service.label[0]?.toUpperCase() ?? ''}${service.label.slice(1)} name`}
            value={name}
            onChange={(event) => {
              setName(event.target.value);
              setDirty(true);
            }}
          />
          <small
            hidden={!(submitted && nameError)}
            data-testid={`${testIdPrefix}-form-name-error`}
          >
            {nameError}
          </small>
        </div>
        {errorMessage && (
          <div
            className="alert mb-0"
            role="alert"
            data-testid={`${testIdPrefix}-form-error`}
          >
            <span className="error-msg">{errorMessage}</span>
          </div>
        )}
        <div className="mt-6 flex justify-center">
          <button
            className="button button-form"
            type="submit"
            data-testid={`${testIdPrefix}-form-save-button`}
            disabled={save.isPending}
          >
            Save
          </button>
        </div>
      </form>
    </Dialog>
  );
}
